import type { ListItem } from "../../components/List/List"
import { ExtraServiceEntity } from "../ExtraService/ExtraServiceEntity"
import { MemoryClass } from "./MemoryClass"
import { convertToLocalMoney } from "../Generics/Helps"
import { EMoneyConditionType } from "../../Enum/EMoneyConditionType"
import { EEnableStatusType } from "../../Enum/EEnableStatusType"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faBolt, faStar, faTag } from "@fortawesome/free-solid-svg-icons"
import i18next from "i18next"

export class ExtraServiceMemory extends MemoryClass<ExtraServiceEntity> {
  setList = (extraServiceList: ExtraServiceEntity[]) => {
    this.listData = extraServiceList
  }

  protected cloneEntity(item: ExtraServiceEntity): ExtraServiceEntity {
    return new ExtraServiceEntity({ ...item })
  }

  getListCards = (all: boolean): ListItem[] => {
    const cards: ListItem[] = []

    let count = 0

    this.listData.forEach((ss) => {
      if (all || ss.status === EEnableStatusType.Active) {
        cards.push(this.getCard(ss, count))
      }
      count++
    })

    return cards
  }

  getCard(ss: ExtraServiceEntity, count: number): ListItem {
    const enable = ss.status === EEnableStatusType.Active
    const hasImage = (ss.iconUrl ?? "").trim().length > 0

    const valueDisplay =
      ss.conditionType === EMoneyConditionType.Percentage ? `${ss.valueBase}%` : convertToLocalMoney(ss.valueBase)

    return {
      isDisable: !enable,
      id: ss.id,
      title: ss.name,
      linkImage: ss.iconUrl,
      index: count,
      dataFilter: [
        ss.name,
        ss.status.toEnableStatusTypeString(),
        ss.isAutomatic ? "automático" : "manual",
        ss.conditionType === EMoneyConditionType.Percentage ? "percentual" : "valor fixo",
      ],
      children: (
        <div
          className={`
          flex flex-col gap-3
          p-3
          rounded-2xl
          transition-all duration-300
          ${
            enable
              ? "bg-gradient-to-r from-orange-50 via-amber-50 to-white hover:shadow-lg hover:scale-[1.01]"
              : "bg-gray-100 opacity-70"
          }
        `}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex gap-3 min-w-0 flex-1">
              <div className="w-10 h-10 rounded-xl bg-orange-100 text-orange-600 flex items-center justify-center shrink-0">
                <FontAwesomeIcon icon={faStar} />
              </div>

              <div className="flex flex-col justify-between flex-1 min-w-0">
                <span className="font-bold text-gray-900 text-base truncate">{ss.name}</span>

                <span className="text-xs text-gray-600 line-clamp-2 font-medium">
                  {ss.description || i18next.t("components:label_no_description")}
                </span>
              </div>
            </div>

            {enable ? (
              <span className="px-3 py-1 text-xs font-bold rounded-full bg-green-100 text-green-700 whitespace-nowrap">
                Ativo
              </span>
            ) : (
              <span className="px-3 py-1 text-xs font-bold rounded-full bg-red-100 text-red-600 whitespace-nowrap">
                Inativo
              </span>
            )}
          </div>

          {ss.iconUrl && (
            <img
              src={ss.iconUrl.toClientRequest()}
              className="w-full h-20 sm:h-24 rounded-xl object-cover border border-orange-100"
            />
          )}

          <div className="border-t border-orange-100 pt-2 flex items-center justify-between gap-2 flex-wrap">
            <span className="text-xl font-extrabold text-orange-700 flex items-center gap-2">
              <FontAwesomeIcon icon={faTag} className="text-xs" />
              {valueDisplay}
            </span>

            <div className="flex gap-2 flex-wrap">
              {ss.isAutomatic && (
                <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-700 font-bold flex items-center gap-1">
                  <FontAwesomeIcon icon={faBolt} className="text-[10px]" />
                  {i18next.t("components:label_isAuto")}
                </span>
              )}

              {!ss.isAutomatic && (
                <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-700 font-semibold">
                  Aplicação manual
                </span>
              )}

              <span className="px-2 py-1 text-xs rounded-full bg-indigo-100 text-indigo-700 font-semibold">
                {ss.conditionType === EMoneyConditionType.Percentage ? "Percentual" : "Valor fixo"}
              </span>

              {hasImage && <span className="px-2 py-1 text-xs rounded-full bg-cyan-100 text-cyan-700">Com imagem</span>}
            </div>
          </div>
        </div>
      ),
    }
  }

  protected getItemId(item: ExtraServiceEntity): number {
    return item.id
  }
}
